/**
 * Lejek konwersji — dwa panele, bo to są dwie różne rzeczy.
 *
 * ## Dlaczego to nie jest trapez
 *
 * Klasyczny lejek rysuje szerokość słupka jako udział w ruchu. Przy realnych
 * liczbach portfolio (5 600 wejść → 19 płacących) ostatnie trzy etapy mają
 * po pół piksela i nie da się ich porównać — a to właśnie one są odpowiedzią
 * na pytanie „gdzie tracimy ludzi".
 *
 * Dlatego **długość paska to konwersja z poprzedniego etapu**, na wspólnej
 * skali 0–100%. Najkrótszy pasek jest najgorszym krokiem i widać to od razu,
 * niezależnie od rzędu wielkości. Udział w całym ruchu jest przy liczbie,
 * osobno i podpisany, żeby nie mylił się z konwersją.
 *
 * ## Kolor
 *
 * Etapy lejka mają kolejność — zamiana ich miejscami zmieniłaby znaczenie —
 * więc niosą rampę porządkową (`--funnel-1..6`), nie sześć kolorów
 * kategorycznych. Sześć niezależnych barw sugerowałoby sześć niezależnych
 * bytów. Rampa idzie od ciemnej u góry do jasnej na dole: na czarnym tle
 * jaśniejszy znaczy ważniejszy, a najważniejszy jest płacący klient.
 *
 * ## Brak danych
 *
 * Etap bez źródła dostaje pusty tor i powód, nigdy zera. Zero na drugim kroku
 * czyta się jako stuprocentowy odpad, czyli katastrofę produktową, której
 * nie było.
 */

import { useId, useState } from "react";

import { count, money, percent } from "../lib/format";
import type { Funnel, FunnelStage, FunnelStock } from "../lib/types";
import { Empty } from "./ui";

const RAMP = [
  "--funnel-1",
  "--funnel-2",
  "--funnel-3",
  "--funnel-4",
  "--funnel-5",
  "--funnel-6",
];

function tone(index: number, total: number): string {
  // Rampa ma sześć stopni; przy krótszym lejku bierzemy je równomiernie,
  // żeby pierwszy etap zawsze był najciemniejszy, a ostatni najjaśniejszy.
  if (total <= 1) return `var(${RAMP[RAMP.length - 1]})`;
  const step = Math.round((index / (total - 1)) * (RAMP.length - 1));
  return `var(${RAMP[step]})`;
}

/* ── Lejek okresu ──────────────────────────────────────────────────────────── */

export function FunnelFlow({ stages }: { stages: FunnelStage[] }) {
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  if (!stages.length) return <Empty title="Brak danych do lejka" />;

  const measured = stages.filter((stage) => stage.available);
  const worst = weakestStep(stages);

  return (
    <div className="funnel">
      <ol className="funnel__list">
        {stages.map((stage, index) => (
          <FunnelRow
            key={stage.key}
            stage={stage}
            color={tone(index, stages.length)}
            isTop={stage.key === measured[0]?.key}
            isWorst={stage.key === worst}
          />
        ))}
      </ol>

      <div className="funnel__foot">
        <button
          type="button"
          className="btn btn--quiet"
          aria-expanded={showTable}
          aria-controls={tableId}
          onClick={() => setShowTable((value) => !value)}
        >
          {showTable ? "ukryj tabelę" : "pokaż jako tabelę"}
        </button>
      </div>

      {showTable && (
        <div className="table-wrap" id={tableId}>
          <table className="data">
            <caption className="visually-hidden">
              Etapy lejka z liczbami, konwersją i odpadem
            </caption>
            <thead>
              <tr>
                <th>Etap</th>
                <th className="num">Liczba</th>
                <th className="num">Konwersja</th>
                <th>Wobec etapu</th>
                <th className="num">Odpad</th>
                <th className="num">Udział w ruchu</th>
              </tr>
            </thead>
            <tbody>
              {stages.map((stage) => (
                <tr key={stage.key}>
                  <th scope="row">{stage.label}</th>
                  <td className="num">
                    {stage.value === null ? (
                      <span className="dim" title={stage.reason ?? undefined}>
                        brak źródła
                      </span>
                    ) : (
                      count(stage.value)
                    )}
                  </td>
                  <td className="num">
                    {stage.conversion.available &&
                    stage.conversion.value !== null
                      ? percent(stage.conversion.value, 1)
                      : "—"}
                  </td>
                  <td className="dim">{stage.compared_to_label ?? "—"}</td>
                  <td className="num">
                    {stage.dropped === null ? "—" : count(stage.dropped)}
                  </td>
                  <td className="num">
                    {stage.share_of_top.available &&
                    stage.share_of_top.value !== null
                      ? percent(stage.share_of_top.value, 2)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Najsłabszy krok lejka: najniższa konwersja spośród zmierzonych.
 *
 * Wyróżniamy dokładnie jeden. Podświetlenie trzech „słabych" etapów nie jest
 * podpowiedzią, tylko kolejną tabelą do przeczytania.
 */
function weakestStep(stages: FunnelStage[]): string | null {
  let worst: FunnelStage | null = null;
  for (const stage of stages) {
    const value = stage.conversion.value;
    if (!stage.conversion.available || value === null) continue;
    if (worst === null || value < (worst.conversion.value ?? 1)) worst = stage;
  }
  return worst?.key ?? null;
}

function FunnelRow({
  stage,
  color,
  isTop,
  isWorst,
}: {
  stage: FunnelStage;
  color: string;
  isTop: boolean;
  isWorst: boolean;
}) {
  const conversion = stage.conversion;
  const hasConversion = conversion.available && conversion.value !== null;
  // Pasek szczytu lejka jest pełny: nie ma nad nim etapu, wobec którego
  // liczyłaby się konwersja.
  const width = isTop
    ? 1
    : hasConversion
      ? Math.min(conversion.value ?? 0, 1)
      : 0;

  return (
    <li className={stage.available ? "fstage" : "fstage fstage--empty"}>
      <div className="fstage__head">
        <span className="fstage__label" title={stage.source}>
          {stage.label}
          {isWorst && (
            <span className="fstage__flag" title="Najniższa konwersja w lejku">
              najsłabszy krok
            </span>
          )}
        </span>
        <span className="fstage__value">
          {stage.value === null ? (
            <span className="fstage__missing">brak źródła</span>
          ) : (
            count(stage.value)
          )}
        </span>
      </div>

      {/* Tor jest siatką, nie flexem: pasek musi mieć zawsze tę samą podstawę
          100%, niezależnie od tego, jak długa jest etykieta obok. W flexie
          pasek „100%" kurczył się o szerokość napisu i etapy przestawały
          leżeć na wspólnej skali — czyli wykres tracił jedyną rzecz, po
          którą się go rysuje. */}
      <div className="fstage__track">
        <div className="fstage__rail">
          <div
            className="fstage__bar"
            style={{
              width: `${Math.max(width * 100, stage.available ? 1.5 : 0)}%`,
              background: color,
            }}
          />
        </div>
        {stage.available && (
          <span className="fstage__pct">
            {isTop
              ? "szczyt lejka"
              : hasConversion
                ? percent(conversion.value ?? 0, 1)
                : "—"}
          </span>
        )}
      </div>

      <p className="fstage__foot">
        {stage.value === null ? (
          <span className="fstage__reason">{stage.reason}</span>
        ) : (
          <>
            {!isTop && stage.compared_to_label && (
              <span>z: {stage.compared_to_label}</span>
            )}
            {stage.dropped !== null && stage.dropped > 0 && (
              <span className="fstage__drop">
                −{count(stage.dropped)} nie doszło dalej
              </span>
            )}
            {stage.share_of_top.available &&
              stage.share_of_top.value !== null &&
              !isTop && (
                <span>{percent(stage.share_of_top.value, 2)} całego ruchu</span>
              )}
            {stage.note && <span className="fstage__warn">{stage.note}</span>}
          </>
        )}
      </p>
    </li>
  );
}

/* ── Stan bazy ─────────────────────────────────────────────────────────────── */

/**
 * Ile kont żyje dziś i na czym siedzą.
 *
 * Świadomie **nie** jest to szósty słupek lejka. Lejek sumuje przepływ okresu,
 * a to jest stan jednego dnia; podzielenie jednego przez drugie dałoby
 * „konwersję", która rośnie sama przy wydłużeniu zakresu dat.
 */
export function FunnelStockPanel({
  stock,
  currency,
}: {
  stock: FunnelStock;
  currency: string;
}) {
  // Bez kont darmowych nie wiadomo, jak duża jest baza, więc proporcje paska
  // byłyby zmyślone. Zostaje tabela płacących z pustym wierszem darmowych.
  const freeKnown = stock.free_accounts.available;

  if (!stock.accounts_total) {
    return freeKnown ? (
      <Empty
        title="Nie ma jeszcze żadnych kont"
        hint="Lejek zapełni się po pierwszej rejestracji."
      />
    ) : (
      <Empty
        title="Brak kont płacących, konta darmowe nieznane"
        hint={stock.free_accounts.reason ?? undefined}
      />
    );
  }

  const segments = barSegments(stock);

  return (
    <div className="tiers">
      {freeKnown && (
        <>
          <div className="tiers__bar" role="img" aria-label={ariaForTiers(stock)}>
            {segments.map((segment, index) => (
              <span
                key={segment.key}
                className="tiers__segment"
                style={{
                  width: `${segment.share * 100}%`,
                  background: tone(index, segments.length),
                }}
                title={`${segment.label}: ${count(segment.accounts)}`}
              />
            ))}
          </div>
          <p className="tiers__legend">
            {segments.map((segment, index) => (
              <span key={segment.key}>
                <i
                  className="tiers__dot"
                  style={{ background: tone(index, segments.length) }}
                  aria-hidden="true"
                />
                {segment.label}
              </span>
            ))}
          </p>
        </>
      )}

      {/* Nazwy planów na poziomie portfolio bywają długie („Kurniki.online ·
          Gospodarstwo"), a komórki tabeli nie łamią wierszy. Bez własnego
          przewijania tabela rozpychałaby całą stronę w poziomie na telefonie. */}
      <div className="table-wrap">
        <table className="data data--tight">
          <caption className="visually-hidden">
            Konta w podziale na plany
          </caption>
          <thead>
            <tr>
              <th>Plan</th>
              <th className="num">Konta</th>
              <th className="num">Udział</th>
              <th className="num">MRR</th>
            </tr>
          </thead>
          <tbody>
            {stock.tiers.map((tier) => (
              <tr key={tier.key}>
                <th scope="row">{tier.label}</th>
                <td className="num">
                  {tier.is_free && !freeKnown ? (
                    <span className="dim" title={stock.free_accounts.reason ?? undefined}>
                      brak danych
                    </span>
                  ) : (
                    count(tier.accounts)
                  )}
                </td>
                <td className="num">
                  {tier.share === null ? "—" : percent(tier.share, 1)}
                </td>
                <td className="num">
                  {tier.is_free ? (
                    <span className="dim">—</span>
                  ) : (
                    money(tier.mrr_reporting_minor, currency, { compact: true })
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Razem</th>
              <td className="num">{count(stock.accounts_total)}</td>
              <td className="num">
                {stock.paid_share.available && stock.paid_share.value !== null
                  ? `${percent(stock.paid_share.value, 1)} płaci`
                  : "—"}
              </td>
              <td className="num">
                {money(
                  stock.mrr_reporting_minor,
                  currency,
                  { compact: true },
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

type Segment = { key: string; label: string; accounts: number; share: number };

/**
 * Segmenty paska stanu bazy.
 *
 * Rampa ma sześć stopni. Przy siedmiu planach — a tyle ma portfolio z trzema
 * produktami — dwa sąsiednie segmenty dostałyby ten sam kolor i pasek
 * przestałby cokolwiek rozdzielać. Dlatego powyżej sześciu pozycji pasek
 * odpowiada na pytanie prostsze i ważniejsze: **jaka część bazy płaci**.
 * Rozbicie na plany zostaje w tabeli pod spodem, gdzie identyczność niesie
 * nazwa, nie barwa.
 */
function barSegments(stock: FunnelStock): Segment[] {
  const visible = stock.tiers.filter((tier) => tier.accounts > 0);
  if (visible.length <= 6) {
    return visible.map((tier) => ({
      key: tier.key,
      label: tier.label,
      accounts: tier.accounts,
      share: tier.share ?? 0,
    }));
  }
  const total = stock.accounts_total || 1;
  return [
    {
      key: "free",
      label: "Konta darmowe",
      accounts: stock.accounts_free,
      share: stock.accounts_free / total,
    },
    {
      key: "paid",
      label: "Konta płatne",
      accounts: stock.accounts_paid,
      share: stock.accounts_paid / total,
    },
  ];
}

function ariaForTiers(stock: FunnelStock): string {
  const parts = stock.tiers
    .filter((tier) => tier.accounts > 0)
    .map((tier) => `${tier.label}: ${tier.accounts}`);
  return `Podział ${stock.accounts_total} kont na plany. ${parts.join(", ")}.`;
}

/* ── Podpowiedź konfiguracyjna ─────────────────────────────────────────────── */

const STEP_LABELS: Record<string, string> = {
  demo: "demo",
  signup: "rejestracja",
};

/**
 * Czego brakuje, żeby lejek był kompletny.
 *
 * Pusty etap bez wyjaśnienia wygląda jak awaria dashboardu. Ta lista mówi,
 * który produkt nie ma ustawionego którego kroku i gdzie się to ustawia.
 */
export function FunnelSetupNote({ setup }: { setup: Funnel["setup"] }) {
  if (!setup.length) return null;
  return (
    <p className="gap-note">
      Lejek jest niepełny, bo nie wszystkie produkty mają wskazane adresy kroków
      pośrednich:{" "}
      {setup
        .map(
          (item) =>
            `${item.name} (${item.missing_steps.map((step) => STEP_LABELS[step] ?? step).join(", ")})`,
        )
        .join(", ")}
      . Uzupełnisz je na zakładce Wdrożenie.
    </p>
  );
}
